import { AnimationKeys } from '../../../../../stores/animation/settings/config';
import { ENVIRONMENT } from '../../../../../utils/environment';
import { getImageUrlWithBucketPrefix } from '../../../../../utils/images';

export const COIN_BACK_RIVE_SRC = getImageUrlWithBucketPrefix(`${ENVIRONMENT}/assets/rive/coinback_v9.riv`);
export const COIN_BACK_BOOSTER_RIVE_SRC = getImageUrlWithBucketPrefix(
  `${ENVIRONMENT}/assets/rive/coinback_booster_v9.riv`,
);

export const COIN_BACK_ARTBOARD = 'Main';
export const COIN_BACK_STATE_MACHINE = 'State Machine 1';

export const CLOSE_DONE_TRIGGER = 'popupClose';

export enum CoinBackMode {
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
  LOCKED = 'LOCKED',
}

export const COIN_BACK_VM = {
  mode: 'mode',
  isClaimPartialCoins: 'isClaimPartialCoins', // bool: show the partial-claim (keep/claim-half) choice layout
  timer: 'timer',
  activate: 'activate',
  close: 'close',
  infoToggle: 'infoToggle',
  tooltipToggle: 'tooltipToggle',
  next: 'next',
  previous: 'previous',
  info: 'info',
  balance: 'balance',
  btnLayout: 'btnLayout',
  multiplier: 'multiplier',
} as const;

export const BALANCE_SECTIONS = {
  locked: 'locked',
  unlocked: 'unlocked',
  total: 'total',
} as const;

export const BALANCE_VM = {
  header: 'header',
  ccAmount: 'ccAmount',
  scAmount: 'scAmount',
} as const;

export const INFO_VM = {
  header1: 'header1',
  body1: 'body1',
  header2: 'header2',
  body2: 'body2',
  header3: 'header3',
  header4: 'header4',
  lockedCC: 'lockedCC',
  lockedSC: 'lockedSC',
  unlockedCC: 'unlockedCC',
  unlockedSC: 'unlockedSC',
} as const;

export const BUTTON_VM = {
  awesome: 'awesome',
  claim: 'claim',
  keep: 'keep',
  claimHalf: 'claimHalf',
} as const;

export const BUTTON_VM_FIELDS = {
  text: 'text',
  color: 'color',
} as const;

export const mapAnimationKeyToMode = (key: AnimationKeys): CoinBackMode => {
  switch (key) {
    case AnimationKeys.SAFE_LOCKED:
      return CoinBackMode.LOCKED;
    case AnimationKeys.SAFE_CLOSE:
    case AnimationKeys.SAFE_CLOSE_CLAIM:
      return CoinBackMode.CLOSED;
    case AnimationKeys.SAFE_OPEN:
    case AnimationKeys.SAFE_OPEN_KEEP:
    case AnimationKeys.SAFE_OPEN_CLAIM:
    default:
      return CoinBackMode.OPEN;
  }
};

export type CoinBackSemanticAction = 'claim' | 'keep' | 'claimHalf' | 'awesome' | 'close';

export type ParsedRiveSignal = { group: string; action: string; interaction: string };

export const parseRiveSignal = (name: string): ParsedRiveSignal | null => {
  const parts = name.split('/');
  if (parts.length !== 3 || parts.some((part) => !part)) return null;
  const [group, action, interaction] = parts;
  return { group, action, interaction };
};

const RIVE_ACTION_TO_SEMANTIC: Record<string, CoinBackSemanticAction> = {
  claim: 'claim',
  keep: 'keep',
  awesome: 'awesome',
  claimhalf: 'claimHalf',
  claimpartial: 'claimHalf',
  claim_half: 'claimHalf',
  claim_partial: 'claimHalf',
  close: 'close',
};

export const mapRiveSignalToSemantic = (name: string): CoinBackSemanticAction | null => {
  const parsed = parseRiveSignal(name);
  const action = (parsed ? parsed.action : name).toLowerCase();
  return RIVE_ACTION_TO_SEMANTIC[action] ?? null;
};

export const ACTION_TRIGGER_PATHS: Record<CoinBackSemanticAction, string> = {
  claim: 'btnLayout/claim/click',
  claimHalf: 'btnLayout/claimHalf/click',
  keep: 'btnLayout/keep/click',
  awesome: 'btnLayout/awesome/click',
  close: 'close',
};

const REQUIRED_RIVE_ACTIONS: CoinBackSemanticAction[] = ['claim', 'keep', 'awesome', 'close'];

export const IS_RIVE_INTERACTION_WIRED = REQUIRED_RIVE_ACTIONS.every((action) =>
  Object.values(RIVE_ACTION_TO_SEMANTIC).includes(action),
);

export type CoinBackUiSignal = 'info' | 'timer' | 'arrowLeft' | 'arrowRight';

// UI taps the .riv fires as VM OUTPUT triggers (analytics only — the .riv handles the
// visual toggle itself). Info tooltip → `infoToggle`; timer tooltip → `tooltipToggle`.
// The carousel arrows (next/previous) are handled internally and emit no outbound trigger
// yet, so they aren't mapped.
export const UI_TRIGGER_PATHS: Partial<Record<CoinBackUiSignal, string>> = {
  info: COIN_BACK_VM.infoToggle,
  timer: COIN_BACK_VM.tooltipToggle,
};
